/**
 * FloatingToolbarCenter - Center floating toolbar (promoted SelectionToolbar)
 * 
 * TWO MODES:
 * - IDLE: Top center, shows run button only
 * - SELECTION: Top of selected group, shows run button + cost + selected nodes
 */

import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useReactFlow, useStore, useViewport } from '@xyflow/react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useExecution } from '../../contexts/ExecutionContext';
import { useAutoSaveContext } from '../../contexts/AutoSaveContext';
import { getNodeCreditCost } from '../../utils/selectionCostUtils';
import { getBillableNodes } from '../../utils/billableNodesUtils';
import { CreditsIcon } from './CreditsIcon';
import { confirmDialogService } from './ConfirmDialog';
import './FloatingToolbarCenter.css';
import { recordUserAction } from '../../services/FlowHistoryRef';

interface FloatingToolbarCenterProps {
  onDelete?: () => void;
  onRunFlow: () => void;
  totalFlowCredits: number;
  isMobile?: boolean;
  onCopyNode?: () => void;
  onDeselectAll?: () => void;
  selectedEdgeCount?: number;
}

// Hidden 2026-05 — displayed values are incorrect; pending recalc rebuild.
const SHOW_FLOATING_CREDITS = false;

export function FloatingToolbarCenter({ onDelete, onRunFlow, totalFlowCredits, isMobile = false, onCopyNode, onDeselectAll, selectedEdgeCount = 0 }: FloatingToolbarCenterProps) {
  const { setNodes, setEdges, getEdges, getNodes } = useReactFlow();
  const { runSelectedNodes, isExecuting, cancel } = useExecution();
  const { markDirty } = useAutoSaveContext();
  const { x: viewX, y: viewY, zoom } = useViewport();
  
  // Get selected nodes from store
  const selectedNodes = useStore((state) => 
    state.nodes.filter((node) => node.selected)
  );
  
  // Get only billable nodes (nodes that actually consume credits)
  const billableNodes = useMemo(() => {
    return getBillableNodes(selectedNodes);
  }, [selectedNodes]);
  
  // Calculate bounding box of selected nodes and convert to screen coordinates
  const selectionPosition = useMemo(() => {
    if (selectedNodes.length === 0) return null;
    
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    
    selectedNodes.forEach((node) => {
      const width = node.measured?.width ?? node.width ?? 300;
      
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
    });
    
    // Convert flow coordinates to screen coordinates
    const centerX = ((minX + maxX) / 2) * zoom + viewX;
    const topY = minY * zoom + viewY;
    
    return {
      centerX,
      topY
    };
  }, [selectedNodes, viewX, viewY, zoom]);
  
  // Calculate total credit cost (only for billable nodes).
  // ElevenLabs TTS is character-billed off its connected text input, so the
  // graph has to be passed in — without it a Voice node fed by a text node
  // quotes the 1-credit floor while the server bills the whole paragraph.
  const totalCredits = useMemo(() => {
    const ctx = { edges: getEdges(), nodes: getNodes() };
    return billableNodes.reduce((sum, node) => {
      return sum + getNodeCreditCost(node, ctx);
    }, 0);
  }, [billableNodes, getEdges, getNodes]);
  
  // Determine mode: idle (nothing selected) or selection
  // Mobile: show action bar on any selection (1+ node or edge)
  // Desktop: show only when 2+ nodes selected
  const isSelectionMode = isMobile
    ? (selectedNodes.length >= 1 || selectedEdgeCount >= 1)
    : selectedNodes.length >= 2;
  
  const handleRunSelected = () => {
    if (isExecuting) return;
    
    const selectedIds = selectedNodes.map(n => n.id);
    runSelectedNodes(selectedIds);
  };
  
  const handleDeleteSelected = async () => {
    // Show confirmation dialog
    const confirmed = await confirmDialogService.confirm({
      title: 'Delete Selected Nodes?',
      message: `Are you sure you want to delete ${selectedNodes.length} node${selectedNodes.length !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) {
      return;
    }
    
    if (onDelete) {
      onDelete();
    } else {
      // Default delete behavior. This edits the graph with a direct setNodes /
      // setEdges rather than through React Flow's onNodesChange, so nothing
      // else marks the flow dirty — and CreativeNodeFlow renders this toolbar
      // without an `onDelete`, so this is the branch that always runs. Without
      // the mark the deleted nodes came back on the next load.
      const selectedIds = new Set(selectedNodes.map(n => n.id));
      recordUserAction(selectedIds.size === 1 ? 'Delete node' : `Delete ${selectedIds.size} nodes`);
      setNodes((nodes) => nodes.filter((n) => !selectedIds.has(n.id)));
      setEdges((edges) => edges.filter((e) =>
        !selectedIds.has(e.source) && !selectedIds.has(e.target)
      ));
      markDirty();
    }
  };
  
  return createPortal(
    <div 
      className={`floating-toolbar-center ${isSelectionMode ? 'selection-mode' : 'idle-mode'}`}
      style={
        isSelectionMode && selectionPosition
          ? isMobile
            ? {
                // Mobile selection mode: fixed bottom, centered
                position: 'fixed',
                left: '50%',
                bottom: 'calc(var(--mobile-bottom-nav-height, 56px) + 40px + 8px)',
                top: 'auto',
                transform: 'translateX(-50%)',
              }
            : {
                // Desktop selection mode: position above selected group
                position: 'fixed',
                left: selectionPosition.centerX,
                top: Math.max(60, selectionPosition.topY - 50),
                transform: 'translateX(-50%)',
              }
          : {
              // Idle mode: bottom right (below minimap)
              position: 'fixed',
              right: '12px',
              bottom: '12px',
              transform: 'none',
            }
      }
    >
      <div className="floating-toolbar-center-content">
        {isSelectionMode ? (
          <>
            {/* Selection Mode: Node count + Credits + Run + Delete */}
            <span className="toolbar-center-count" title={`${selectedNodes.length} selected, ${billableNodes.length} will process`}>
              {billableNodes.length === selectedNodes.length 
                ? `${selectedNodes.length} nodes` 
                : `${billableNodes.length} / ${selectedNodes.length} nodes`}
            </span>
            
            <div className="toolbar-center-separator" />

            {/* Hidden 2026-05 — displayed values are incorrect; pending recalc rebuild. */}
            {SHOW_FLOATING_CREDITS && (
              <>
                <span className="toolbar-center-credits" title="Estimated credit cost to run billable nodes">
                  <CreditsIcon size={12} /> ~{totalCredits}
                </span>

                <div className="toolbar-center-separator" />
              </>
            )}

            <button
              className="toolbar-center-btn run-btn"
              data-tutorial="run-flow-button"
              onClick={handleRunSelected}
              disabled={isExecuting}
              title={`Run ${billableNodes.length} billable node${billableNodes.length !== 1 ? 's' : ''}`}
            >
              <PlayArrowIcon sx={{ fontSize: 16 }} />
              Run
            </button>
            
            <button
              className="toolbar-center-btn delete-btn"
              onClick={handleDeleteSelected}
              title="Delete selected nodes"
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </button>

            {/* Copy button — mobile only, single node */}
            {isMobile && selectedNodes.length === 1 && onCopyNode && (
              <button
                className="toolbar-center-btn copy-btn"
                onClick={onCopyNode}
                title="Duplicate node"
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </button>
            )}

            {/* Deselect button — mobile only */}
            {isMobile && onDeselectAll && (
              <button
                className="toolbar-center-btn deselect-btn"
                onClick={onDeselectAll}
                title="Deselect all"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </>
        ) : (
          <>
            {/* Idle Mode: Total flow credits + Run button */}
            {/* Hidden 2026-05 — displayed values are incorrect; pending recalc rebuild. */}
            {SHOW_FLOATING_CREDITS && (
              <>
                <span className="toolbar-center-credits" title="Estimated credit cost to run the whole flow">
                  <CreditsIcon size={12} /> ~{totalFlowCredits}
                </span>

                <div className="toolbar-center-separator" />
              </>
            )}

            <button
              className="toolbar-center-btn run-btn"
              data-tutorial="run-flow-button"
              onClick={() => {
                if (isExecuting) {
                  cancel();
                } else {
                  onRunFlow();
                }
              }}
              title={isExecuting ? "Stop Execution (Esc)" : "Run Knod (Ctrl+Shift+Enter)"}
            >
              {isExecuting ? <StopIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
              {isExecuting ? 'Stop' : 'Run'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
